import { useResetRequest } from "@/features/password/password.hooks";
import { usePasswordStore } from "@/features/password/password.store";
import { rootRoute } from "@/routes";
import { validatePassword } from "@/shared/lib/passwordPolicy";
import { CommonHeader } from "@/shared/ui/composites";
import { TextInputField } from "@/shared/ui/composites";
import { Button, Divider, Spinner, VStack } from "@/shared/ui/primitives";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Desktop = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const resetRequest = useResetRequest();
	const setPendingResetRequest = usePasswordStore((state) => state.setPendingResetRequest);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [confirmError, setConfirmError] = useState("");
	const [error, setError] = useState("");

	useEffect(() => {
		setPendingResetRequest(null);
	}, [setPendingResetRequest]);

	const handleReset = () => {
		const normalizedEmail = email.trim();
		const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
		const pwError = validatePassword(password, normalizedEmail);
		const isConfirmValid = password === confirmPassword;
		setEmailError(isEmailValid ? "" : t("auth.emailError"));
		setPasswordError(pwError ? t(pwError) : "");
		setConfirmError(isConfirmValid ? "" : t("auth.passwordConfirmError"));
		if (!isEmailValid || pwError || !isConfirmValid) return;
		setError("");
		resetRequest.mutate(
			{ email: normalizedEmail, password },
			{
				onSuccess: () => {
					setPendingResetRequest({ email: normalizedEmail, password });
					navigate({
						to: "/verify",
						search: { email: normalizedEmail, redirect: "/login", type: "reset-password" },
						replace: true,
					});
				},
				onError: (err) => {
					if (err instanceof AxiosError) {
						const code = err.response?.data?.code as string | undefined;
						const message = err.response?.data?.error as string | undefined;

						if (code === "USER_NOT_FOUND") {
							setEmailError(t("auth.emailNotRegistered"));
							setError("");
							return;
						}

						if (code === "OAUTH_CONFLICT") {
							setEmailError(t("auth.emailOAuthConflict"));
							setError("");
							return;
						}

						if (message) {
							setError(message);
							return;
						}
					}

					setError(t("auth.resetError"));
				},
			},
		);
	};

	return (
		<VStack className="w-full h-full bg-bg-weak px-[20px] md:px-0">
			<form
				className="flex-col w-full max-w-[480px] rounded-[16px] outline-outline p-[20px] gap-[24px]"
				onSubmit={(e) => {
					e.preventDefault();
					handleReset();
				}}
			>
				<CommonHeader
					title={t("auth.resetPassword")}
					description={t("auth.resetPasswordDescription")}
					onTap={() => navigate({ to: "/login", replace: true })}
				/>
				<Divider className="w-full" />
				<VStack className="w-full gap-[10px]">
					<TextInputField
						label={t("auth.email")}
						placeholder={t("auth.emailHint")}
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
							setEmailError("");
							setError("");
						}}
						error={!!emailError}
						errorMessage={emailError}
					/>
					<TextInputField
						label={t("auth.password")}
						placeholder={t("auth.passwordHint")}
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setPasswordError("");
							setError("");
						}}
						type="password"
						error={!!passwordError}
						errorMessage={passwordError}
					/>
					<TextInputField
						label={t("auth.passwordConfirm")}
						placeholder={t("auth.passwordConfirmPlaceholder")}
						value={confirmPassword}
						onChange={(e) => {
							setConfirmPassword(e.target.value);
							setConfirmError("");
							setError("");
						}}
						type="password"
						error={!!confirmError}
						errorMessage={confirmError}
					/>
				</VStack>
				{error && (
					<span className="w-full px-[10px] py-[6px] text-sm text-dg-strong font-normal">
						{error}
					</span>
				)}
				<Button
					variant="primary"
					size="lg"
					className="w-full"
					type="submit"
					disabled={resetRequest.isPending}
				>
					{resetRequest.isPending ? <Spinner size="sm" /> : t("auth.resetPassword")}
				</Button>
			</form>
		</VStack>
	);
};

export const resetPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reset-password",
	component: Desktop,
});
